import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import './HeroCarousel.css';

/**
 * Auto-rotating hero carousel (matches Kanzen / Hobbiesville pattern).
 * Slides come from props, active admin banners, or the cached latest-set
 * Pokemon TCG showcase.
 *
 *   <HeroCarousel slides={[
 *     { title: 'New Set: Stellar Crown', subtitle: 'Out now', image: '/img/banner.jpg', cta: 'Shop now', href: '/shop/pokemon-cards' },
 *     ...
 *   ]} />
 *
 * Falls back to a sensible default set if no slides supplied.
 */
const DEFAULT_SLIDES = [
  {
    title: 'Pokemon TCG — singles & sealed',
    subtitle: 'Daily restocks · graded slabs · 1,000+ cards in stock',
    image: '/images/banner-pokemon.jpg',
    cta: 'Shop Pokemon',
    href: '/shop/pokemon-cards',
    bg: 'linear-gradient(135deg, #3b4cca 0%, #ffde00 100%)',
  },
  {
    title: 'Sell your collection',
    subtitle: 'Instant quotes · paid in cash or +20% in store credit',
    image: '/images/banner-sell.jpg',
    cta: 'Get a quote',
    href: '/sell',
    bg: 'linear-gradient(135deg, #27ae60 0%, #2ecc71 100%)',
  },
  {
    title: 'Live auctions',
    subtitle: 'Rare slabs and grail cards — bidding daily',
    image: '/images/banner-auctions.jpg',
    cta: 'Browse auctions',
    href: '/auctions',
    bg: 'linear-gradient(135deg, #c0392b 0%, #e74c3c 100%)',
  },
  {
    title: 'Build a deck',
    subtitle: 'Use our deck builder to assemble + cart-fill from inventory',
    image: '/images/banner-decks.jpg',
    cta: 'Open deck builder',
    href: '/decks',
    bg: 'linear-gradient(135deg, #8e44ad 0%, #9b59b6 100%)',
  },
];

const SERIES_BACKDROPS = [
  'linear-gradient(135deg, #243b6b 0%, #111827 50%, #d7a528 100%)',
  'linear-gradient(135deg, #4a148c 0%, #1a0033 50%, #ff6f00 100%)',
  'linear-gradient(135deg, #006064 0%, #0d1b2a 50%, #ef5350 100%)',
  'linear-gradient(135deg, #1b5e20 0%, #0a1f0c 50%, #ffd54f 100%)',
  'linear-gradient(135deg, #880e4f 0%, #1a0010 50%, #00bcd4 100%)',
];

function formatReleaseDate(raw) {
  if (!raw) return '';
  const ts = Date.parse(String(raw).replace(/\//g, '-'));
  if (!Number.isFinite(ts)) return String(raw);
  return new Date(ts).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' });
}

function seriesSlides(data) {
  const sets = Array.isArray(data?.sets) ? data.sets : [];
  if (!sets.length) return [];
  return sets.map((s, i) => {
    const dateStr = formatReleaseDate(s.releaseDate);
    const subtitle = s.isUpcoming
      ? `Coming ${dateStr}${s.daysUntil ? ` · ${s.daysUntil} day${s.daysUntil === 1 ? '' : 's'} away` : ''}`
      : `${data.series} series · Released ${dateStr}`;
    return {
      title: s.name,
      subtitle,
      art: s.logo || s.symbol || null,
      artVariant: 'logo',
      badge: s.isUpcoming ? 'Coming soon' : null,
      cta: s.isUpcoming ? 'Get notified' : 'Shop set',
      href: s.isUpcoming ? `/shop?preorder=1&set=${encodeURIComponent(s.id)}` : `/shop?set=${encodeURIComponent(s.id)}`,
      bg: SERIES_BACKDROPS[i % SERIES_BACKDROPS.length],
    };
  });
}

export default function HeroCarousel({ slides: slidesProp, autoplay = true, intervalMs = 6000 }) {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const [slides, setSlides] = useState(slidesProp || DEFAULT_SLIDES);

  useEffect(() => {
    if (slidesProp) return;

    const loadLatestSetSlides = () => {
      fetch('/api/categories/series-showcase?series=Mega%20Evolution')
        .then(r => r.ok ? r.json() : null)
        .then(data => {
          const next = seriesSlides(data);
          if (next.length) setSlides(next);
        })
        .catch(() => {});
    };

    fetch('/api/site/banners')
      .then(r => r.ok ? r.json() : null)
      .then(rows => {
        if (Array.isArray(rows) && rows.length > 0) {
          setSlides(rows.map(r => ({
            title: r.title, subtitle: r.subtitle, image: r.image_url,
            cta: r.cta_label, href: r.cta_url, bg: r.bg_gradient,
          })));
        } else {
          loadLatestSetSlides();
        }
      })
      .catch(loadLatestSetSlides);
  }, [slidesProp]);

  const go = useCallback((n) => setIdx((idx + n + slides.length) % slides.length), [idx, slides.length]);

  useEffect(() => {
    if (!autoplay || paused || slides.length < 2) return;
    const t = setInterval(() => go(1), intervalMs);
    return () => clearInterval(t);
  }, [autoplay, paused, intervalMs, go, slides.length]);

  if (!slides || !slides.length) return null;
  const s = slides[idx];

  return (
    <section
      className="hero-carousel"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      aria-roledescription="carousel"
      aria-label="Featured promotions"
    >
      <div
        className="hero-carousel__slide"
        style={{
          background: s.bg_color || s.bg,
          backgroundImage: s.image ? `url(${s.image})` : (s.bg_color || s.bg),
          color: s.text_color || undefined,
        }}
        aria-live="polite"
      >
        {s.image && s.overlay_opacity != null && (
          <div style={{
            position: 'absolute', inset: 0,
            background: '#000', opacity: Number(s.overlay_opacity),
            pointerEvents: 'none',
          }} />
        )}
        {s.art && (
          <img
            className={`hero-carousel__art${s.artVariant === 'logo' ? ' hero-carousel__art--logo' : ''}`}
            src={s.art}
            alt=""
            aria-hidden="true"
          />
        )}
        <div className="hero-carousel__content" style={{ position: 'relative' }}>
          {s.badge && <span className="hero-carousel__badge">{s.badge}</span>}
          <h2 className="hero-carousel__title" style={s.text_color ? { color: s.text_color } : undefined}>{s.title}</h2>
          {s.subtitle && <p className="hero-carousel__subtitle" style={s.text_color ? { color: s.text_color, opacity: 0.9 } : undefined}>{s.subtitle}</p>}
          {s.cta && s.href && (
            <Link to={s.href} className="hero-carousel__cta"
              style={(s.button_bg || s.button_text) ? { background: s.button_bg, color: s.button_text } : undefined}>
              {s.cta} →
            </Link>
          )}
        </div>
      </div>

      {slides.length > 1 && (
        <>
          <button
            className="hero-carousel__arrow hero-carousel__arrow--prev"
            onClick={() => go(-1)}
            aria-label="Previous slide"
          >‹</button>
          <button
            className="hero-carousel__arrow hero-carousel__arrow--next"
            onClick={() => go(1)}
            aria-label="Next slide"
          >›</button>
          <ol className="hero-carousel__dots" role="tablist">
            {slides.map((_, i) => (
              <li key={i}>
                <button
                  className={`hero-carousel__dot${i === idx ? ' hero-carousel__dot--active' : ''}`}
                  onClick={() => setIdx(i)}
                  aria-label={`Go to slide ${i + 1}`}
                  aria-selected={i === idx}
                  role="tab"
                />
              </li>
            ))}
          </ol>
        </>
      )}
    </section>
  );
}
